/**
 * Two-Factor Authentication Settings Component
 * Phase 4.1 - User-facing 2FA management
 *
 * Features:
 * - View 2FA status
 * - Enable 2FA with QR code setup
 * - View/regenerate recovery codes
 * - Disable 2FA
 */

import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';
import {
  FiShield,
  FiSmartphone,
  FiKey,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiCopy,
  FiDownload,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
} from 'react-icons/fi';

const TwoFactorSettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup flow state
  const [setupStep, setSetupStep] = useState(null); // null, 'qr', 'verify', 'recovery'
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // Disable flow state
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // Regenerate codes state
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');

  const [processing, setProcessing] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await authAPI.get2faStatus();
      setStatus(res.data.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
      setStatus({ available: false });
    } finally {
      setLoading(false);
    }
  };

  const beginSetup = async () => {
    setProcessing(true);
    try {
      const res = await authAPI.begin2faSetup();
      setSetupData(res.data.data);
      setSetupStep('qr');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start 2FA setup');
    } finally {
      setProcessing(false);
    }
  };

  const verifyAndEnable = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setProcessing(true);
    try {
      const res = await authAPI.verify2faSetup(verifyCode);
      setRecoveryCodes(res.data.data.recoveryCodes);
      setSetupStep('recovery');
      toast.success('Two-factor authentication enabled!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const completeSetup = async () => {
    try {
      await authAPI.markRecoveryCodesViewed();
    } catch (e) {
      // Non-critical
    }
    setSetupStep(null);
    setSetupData(null);
    setVerifyCode('');
    setRecoveryCodes([]);
    fetchStatus();
  };

  const handleDisable = async () => {
    if (!disablePassword || !disableCode) {
      toast.error('Password and verification code are required');
      return;
    }

    setProcessing(true);
    try {
      await authAPI.disable2fa(disablePassword, disableCode);
      toast.success('Two-factor authentication disabled');
      setShowDisable(false);
      setDisablePassword('');
      setDisableCode('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to disable 2FA');
    } finally {
      setProcessing(false);
    }
  };

  const handleRegenerate = async () => {
    if (!regeneratePassword) {
      toast.error('Password is required');
      return;
    }

    setProcessing(true);
    try {
      const res = await authAPI.regenerateRecoveryCodes(regeneratePassword);
      setRecoveryCodes(res.data.data.recoveryCodes);
      setShowRegenerate(false);
      setRegeneratePassword('');
      setSetupStep('recovery');
      toast.success('New recovery codes generated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate new codes');
    } finally {
      setProcessing(false);
    }
  };

  const copyRecoveryCodes = () => {
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Recovery codes copied to clipboard');
  };

  const downloadRecoveryCodes = () => {
    const text = `Hisab OK Hai - Recovery Codes\n\nThese codes can be used to access your account if you lose your authenticator.\nEach code can only be used once.\n\n${recoveryCodes.join('\n')}\n\nGenerated: ${new Date().toLocaleString()}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hisab-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="tfa-section">
        <p style={{ color: '#999' }}>Loading security settings...</p>
      </div>
    );
  }

  if (!status?.available) {
    return (
      <div className="tfa-section">
        <h3><FiShield style={{ marginRight: 8 }} />Two-Factor Authentication</h3>
        <p style={{ color: '#999' }}>Two-factor authentication is not available at this time.</p>
      </div>
    );
  }

  // Setup Flow - QR Code Step
  if (setupStep === 'qr') {
    return (
      <div className="tfa-section">
        <h3><FiSmartphone style={{ marginRight: 8 }} />Set Up Two-Factor Authentication</h3>

        <div className="tfa-setup-steps">
          <div className="tfa-step">
            <span className="step-number">1</span>
            <p>Download an authenticator app like Google Authenticator or Authy</p>
          </div>
          <div className="tfa-step active">
            <span className="step-number">2</span>
            <p>Scan this QR code with your authenticator app</p>
          </div>
        </div>

        <div className="tfa-qr-container">
          {setupData?.qrCodeDataUrl && (
            <img src={setupData.qrCodeDataUrl} alt="2FA QR Code" className="tfa-qr-code" />
          )}
        </div>

        <div className="tfa-manual-entry">
          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 8 }}>
            Can't scan? Enter this key manually:
          </p>
          <div className="tfa-secret-display">
            <code>{showSecret ? setupData?.manualEntryKey : '••••••••••••••••'}</code>
            <button
              type="button"
              className="btn-icon"
              onClick={() => setShowSecret(!showSecret)}
              title={showSecret ? 'Hide' : 'Show'}
            >
              {showSecret ? <FiEyeOff /> : <FiEye />}
            </button>
            {showSecret && (
              <button
                type="button"
                className="btn-icon"
                onClick={() => {
                  navigator.clipboard.writeText(setupData?.manualEntryKey);
                  toast.success('Key copied');
                }}
                title="Copy"
              >
                <FiCopy />
              </button>
            )}
          </div>
        </div>

        <div className="tfa-actions" style={{ marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={() => { setSetupStep(null); setSetupData(null); }}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => setSetupStep('verify')}>
            Next: Verify Code
          </button>
        </div>
      </div>
    );
  }

  // Setup Flow - Verify Step
  if (setupStep === 'verify') {
    return (
      <div className="tfa-section">
        <h3><FiKey style={{ marginRight: 8 }} />Verify Your Authenticator</h3>

        <div className="tfa-setup-steps">
          <div className="tfa-step completed">
            <span className="step-number"><FiCheck /></span>
            <p>QR code scanned</p>
          </div>
          <div className="tfa-step active">
            <span className="step-number">3</span>
            <p>Enter the 6-digit code from your authenticator app</p>
          </div>
        </div>

        <div className="form-group" style={{ maxWidth: 300, margin: '20px auto' }}>
          <input
            type="text"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="tfa-code-input"
            maxLength={6}
            autoFocus
            style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }}
          />
        </div>

        <div className="tfa-actions">
          <button className="btn btn-secondary" onClick={() => setSetupStep('qr')}>
            Back
          </button>
          <button className="btn btn-primary" onClick={verifyAndEnable} disabled={processing || verifyCode.length !== 6}>
            {processing ? 'Verifying...' : 'Enable 2FA'}
          </button>
        </div>
      </div>
    );
  }

  // Setup Flow - Recovery Codes Step
  if (setupStep === 'recovery') {
    return (
      <div className="tfa-section">
        <h3><FiKey style={{ marginRight: 8 }} />Save Your Recovery Codes</h3>

        <div className="tfa-warning" style={{ marginBottom: 20 }}>
          <FiAlertTriangle style={{ marginRight: 8 }} />
          <strong>Important:</strong> Save these codes in a secure place. Each code can only be used once.
          If you lose access to your authenticator, these are the only way to recover your account.
        </div>

        <div className="tfa-recovery-codes">
          {recoveryCodes.map((code, idx) => (
            <code key={idx} className="tfa-recovery-code">{code}</code>
          ))}
        </div>

        <div className="tfa-actions" style={{ marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={copyRecoveryCodes}>
            <FiCopy style={{ marginRight: 6 }} />
            Copy All
          </button>
          <button className="btn btn-secondary" onClick={downloadRecoveryCodes}>
            <FiDownload style={{ marginRight: 6 }} />
            Download
          </button>
          <button className="btn btn-primary" onClick={completeSetup}>
            I've Saved My Codes
          </button>
        </div>
      </div>
    );
  }

  // Disable Flow
  if (showDisable) {
    return (
      <div className="tfa-section">
        <h3><FiAlertTriangle style={{ marginRight: 8, color: '#E74C3C' }} />Disable Two-Factor Authentication</h3>

        <p style={{ color: '#666', marginBottom: 20 }}>
          This will remove the extra security from your account. You'll need to verify your identity to proceed.
        </p>

        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            placeholder="Enter your password"
          />
        </div>

        <div className="form-group">
          <label>Authenticator Code or Recovery Code</label>
          <input
            type="text"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="Enter code"
          />
        </div>

        <div className="tfa-actions">
          <button className="btn btn-secondary" onClick={() => { setShowDisable(false); setDisablePassword(''); setDisableCode(''); }}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleDisable} disabled={processing}>
            {processing ? 'Disabling...' : 'Disable 2FA'}
          </button>
        </div>
      </div>
    );
  }

  // Regenerate Codes Flow
  if (showRegenerate) {
    return (
      <div className="tfa-section">
        <h3><FiRefreshCw style={{ marginRight: 8 }} />Generate New Recovery Codes</h3>

        <p style={{ color: '#666', marginBottom: 20 }}>
          This will invalidate all your existing recovery codes and generate new ones.
          Enter your password to proceed.
        </p>

        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            value={regeneratePassword}
            onChange={(e) => setRegeneratePassword(e.target.value)}
            placeholder="Enter your password"
            autoFocus
          />
        </div>

        <div className="tfa-actions">
          <button className="btn btn-secondary" onClick={() => { setShowRegenerate(false); setRegeneratePassword(''); }}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleRegenerate} disabled={processing}>
            {processing ? 'Generating...' : 'Generate New Codes'}
          </button>
        </div>
      </div>
    );
  }

  // Main Status View
  return (
    <div className="tfa-section">
      <h3><FiShield style={{ marginRight: 8 }} />Two-Factor Authentication</h3>

      {status.enabled ? (
        <>
          <div className="tfa-status enabled">
            <FiCheck style={{ marginRight: 8 }} />
            <span>Two-factor authentication is <strong>enabled</strong></span>
          </div>

          <div className="tfa-info">
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Enabled on: {status.enabledAt ? new Date(status.enabledAt).toLocaleDateString('en-IN', {
                year: 'numeric', month: 'long', day: 'numeric'
              }) : 'N/A'}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Recovery codes remaining: <strong>{status.recoveryCodesRemaining}</strong>
              {status.recoveryCodesRemaining <= 2 && (
                <span style={{ color: '#E74C3C', marginLeft: 8 }}>
                  <FiAlertTriangle /> Low - consider generating new codes
                </span>
              )}
            </p>
          </div>

          <div className="tfa-actions" style={{ marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setShowRegenerate(true)}>
              <FiRefreshCw style={{ marginRight: 6 }} />
              New Recovery Codes
            </button>
            <button className="btn btn-danger" onClick={() => setShowDisable(true)}>
              <FiX style={{ marginRight: 6 }} />
              Disable 2FA
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="tfa-status disabled">
            <FiX style={{ marginRight: 8 }} />
            <span>Two-factor authentication is <strong>not enabled</strong></span>
          </div>

          <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 12 }}>
            Add an extra layer of security to your account by requiring a code from your phone in addition to your password.
          </p>

          <button className="btn btn-primary" onClick={beginSetup} disabled={processing} style={{ marginTop: 16 }}>
            {processing ? 'Starting...' : 'Enable Two-Factor Authentication'}
          </button>
        </>
      )}
    </div>
  );
};

export default TwoFactorSettings;
